import { Dispatch, FC, ReactNode, SetStateAction, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Dialog, DialogContent, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LogoUploadModalContent from 'src/pages/ViewPartnerPage/components/LogoUpload/ModalContent';
import ConfirmationModal from '../Modals/ConfirmationModal';

import NPIButton from 'src/components/Button';
import { DEFAULT_BORDER } from 'src/constants';
import { IEditPaymentDetailsModalContentProps } from 'src/pages/ViewPartnerPage/components/PaymentDetails/EditPaymentDetailsModalContent';

import HeaderBackground from '../../assets/images/backgrounds/HeaderBackground.jpg';
import { AddRecordButtonConfig } from '../NPIDataGrid/components/CustomToolbar';

interface ReusableGridProps {
    title?: string;
    children: ReactNode;
    EditModalContent?: (props: IEditPaymentDetailsModalContentProps) => JSX.Element;
    hasEdit?: boolean;
    addRecordButtonConfig?: AddRecordButtonConfig;
    hasView?: boolean;
    isEditLogo?: boolean;

    xs?: number;
    maxWidth?: boolean;
    hasBackGroundImage?: boolean;
    setViewPaymentDetails?: Dispatch<SetStateAction<boolean>>;
    initialValuesObject?: unknown;
    handleEdit?: (values: unknown) => void;
    enableContainer?: boolean;
}

const ReusableGrid: FC<ReusableGridProps> = ({
    title,
    children,
    addRecordButtonConfig,
    hasView,
    xs = 6,
    maxWidth,
    EditModalContent,
    hasBackGroundImage,
    setViewPaymentDetails,
    initialValuesObject,
    handleEdit,
    hasEdit = typeof handleEdit === 'function',
    enableContainer = true,
    isEditLogo,
}) => {
    const [openEditModal, setOpenEditModal] = useState(false);
    const theme = useTheme();

    const handleViewPaymentDetails = () => {
        if (setViewPaymentDetails) {
            setViewPaymentDetails((prevState: boolean) => {
                return !prevState;
            });
        }
    };

    const handleEditModal = () => {
        setOpenEditModal(!openEditModal);
    };

    return (
        <Grid
            item
            xs={xs}
            maxWidth={maxWidth ? '100%' : null}
            sx={{
                ...DEFAULT_BORDER,
                boxShadow: '0px 4px 20px rgba(38, 50, 56, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                backgroundImage: hasBackGroundImage ? `url(${HeaderBackground})` : null,
            }}
            {...(enableContainer ? { container: true } : {})} // Conditionally spread `container` prop
        >
            <Grid item container sx={{ padding: '24px 24px' }}>
                {title ? (
                    <Grid item xs={addRecordButtonConfig ? 9 : 11}>
                        <Typography
                            sx={{
                                fontSize: '21px',
                                fontWeight: 500,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {title}
                        </Typography>
                    </Grid>
                ) : null}

                {hasEdit && (
                    <Grid item xs={1} display="flex" justifyContent="right" gap={1}>
                        {hasView && <VisibilityOutlinedIcon onClick={handleViewPaymentDetails} />}
                        {handleEdit && (
                            <EditIcon
                                onClick={handleEditModal}
                                sx={{
                                    '&:hover': {
                                        cursor: 'pointer',
                                    },
                                }}
                            />
                        )}
                        {addRecordButtonConfig && (
                            <Dialog
                                open={openEditModal}
                                onClose={handleEditModal}
                                aria-labelledby="create-note-offer-dialog"
                                sx={{
                                    '& .MuiDialog-paper': {
                                        width: '400px',
                                    },
                                }}
                            >
                                <DialogContent>
                                    <EditModalContent
                                        handleClose={handleEditModal}
                                        buttonText="Edit"
                                        header="Edit"
                                        initialValues={initialValuesObject}
                                        handleFormSubmit={handleEdit}
                                        isSubmitModal
                                    />
                                </DialogContent>
                            </Dialog>
                        )}
                        {isEditLogo && (
                            <ConfirmationModal
                                isOpen={openEditModal}
                                modalWidth="500px"
                                title="Add Partner Logo"
                                message={
                                    <LogoUploadModalContent
                                        handleEditModalClose={handleEditModal}
                                        handleEdit={handleEdit}
                                    />
                                }
                                hideCancelButton
                                hideConfirmButton
                                onCancel={handleEditModal}
                            />
                        )}
                    </Grid>
                )}

                {addRecordButtonConfig && (
                    <Grid item xs={3} display="flex" justifyContent="right">
                        <NPIButton
                            startIcon={<AddIcon />}
                            label={addRecordButtonConfig.addRecordButtonText}
                            onClick={addRecordButtonConfig.addRecordCallBack}
                        />
                    </Grid>
                )}
            </Grid>

            {children}
        </Grid>
    );
};

export default ReusableGrid;
